import { FC } from 'react';
import Button from 'components/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import TooltipWrapper from 'components/TooltipWrapper';

interface IFormButtonsProps {
  btnLabel: string;
  submitTestId?: string;
  primaryActionBtnType?: 'primary' | 'danger';
  hideCancelBtn?: boolean;
  isHorizontal?: boolean;
  isSubmitting?: boolean;
  showBackBtn?: boolean;
  areBtnsCentered?: boolean;
  onCancel?: () => void;
  onGoBack?: () => void;
  isSubmitDisabled?: boolean;
  disabledBtnTooltip?: string;
  showGlobalFieldsError?: boolean;
  extraActionLabel?: string;
  setExitInterviewRef?: (toggle: boolean) => void;
  extraAction?: boolean;
}

/*
 * Abstracted form buttons component, displaying submit button with/without cancel option.
 * It serves different layout based on props.
 */
const FormButtons: FC<IFormButtonsProps> = ({
  btnLabel,
  submitTestId,
  primaryActionBtnType,
  hideCancelBtn,
  isHorizontal,
  isSubmitting,
  showBackBtn,
  areBtnsCentered,
  onCancel,
  onGoBack,
  isSubmitDisabled,
  disabledBtnTooltip,
  showGlobalFieldsError,
  extraActionLabel,
  setExitInterviewRef,
  extraAction,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  if (isHorizontal)
    return (
      <>
        <Row className={areBtnsCentered ? 'text-center' : ''}>
          {showGlobalFieldsError && (
            <Col lg={areBtnsCentered ? 12 : { span: 6, offset: 2 }}>
              <p className="text-error form-text text-muted">{t('form.fillWithValidData')}</p>
            </Col>
          )}
          <Col lg={areBtnsCentered ? 12 : { span: 6, offset: 2 }}>
            <TooltipWrapper
              id="tooltip-submit-horizontal"
              overlay={disabledBtnTooltip ?? ''}
              show={isSubmitDisabled && disabledBtnTooltip ? undefined : false}
            >
              <Button
                disabled={isSubmitDisabled || isSubmitting}
                loading={isSubmitting}
                className={`${extraAction && 'pl-2 pr-2'}`}
                type="submit"
                btnType={primaryActionBtnType ?? 'primary'}
                testId={submitTestId}
              >
                {btnLabel}
              </Button>
            </TooltipWrapper>
            {showBackBtn && (
              <Button
                type="button"
                className="ml-2"
                btnType="secondary"
                outlined
                onClick={() => (onGoBack ? onGoBack() : history.goBack())}
              >
                {t('common.back')}
              </Button>
            )}
            {!hideCancelBtn && (
              <Button
                type="button"
                className="ml-2"
                btnType="light"
                outlined
                onClick={() => (onCancel ? onCancel() : history.goBack())}
              >
                {t('common.cancel')}
              </Button>
            )}
          </Col>
        </Row>
        {extraAction && (
          <Row className={`mt-2${areBtnsCentered ? ' text-center' : ''}`}>
            <Col>
              <Button
                disabled={isSubmitDisabled || isSubmitting}
                loading={isSubmitting}
                type="submit"
                btnType={primaryActionBtnType ?? 'primary'}
                onClick={() => {
                  if (setExitInterviewRef) {
                    setExitInterviewRef(true);
                  }
                }}
              >
                {extraActionLabel}
              </Button>
            </Col>
          </Row>
        )}
      </>
    );

  return (
    <>
      <Row>
        {showGlobalFieldsError && (
          <Col
            sm={12}
            className="text-center"
          >
            <p className="text-error form-text text-muted">{t('form.fillWithValidData')}</p>
          </Col>
        )}
        <Col
          sm={12}
          className="text-center pt-2"
        >
          <TooltipWrapper
            id="tooltip-submit"
            overlay={disabledBtnTooltip ?? ''}
            show={isSubmitDisabled && disabledBtnTooltip ? undefined : false}
          >
            <Button
              disabled={isSubmitDisabled || isSubmitting}
              loading={isSubmitting}
              type="submit"
              className={`${extraAction ? 'pl-2 pr-2' : 'pl-4 pr-4'}`}
              btnType={primaryActionBtnType ?? 'primary'}
              testId={submitTestId}
            >
              {btnLabel}
            </Button>
          </TooltipWrapper>
          {!hideCancelBtn && (
            <Button
              type="button"
              className="ml-2"
              btnType="light"
              outlined
              onClick={() => (onCancel ? onCancel() : history.goBack())}
            >
              {t('common.cancel')}
            </Button>
          )}
        </Col>
      </Row>
      {extraAction && (
        <Row className={`mt-2${areBtnsCentered ? ' text-center' : ''}`}>
          <Col>
            <Button
              disabled={isSubmitDisabled || isSubmitting}
              loading={isSubmitting}
              type="submit"
              btnType={primaryActionBtnType ?? 'primary'}
              onClick={() => {
                if (setExitInterviewRef) {
                  setExitInterviewRef(true);
                }
              }}
            >
              {extraActionLabel}
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
};

export default FormButtons;
